import { useEffect, useRef, useCallback } from 'react';
import { supabase, chatSupabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useOrganizationContext } from '@/contexts/OrganizationContext';
import { toast } from 'sonner';
import { MessageSquare, X } from 'lucide-react';
import { normalizePhone } from '@/utils/phoneUtils';

interface ChatNotificationData {
  chatId: string;
  phone: string;
  contactName: string;
  messagePreview: string;
  messageId: string;
}

export function useChatNotifications(
  selectedChatId: string | null,
  selectedChatPhone: string | null,
  onChatSelect?: (chatId: string, phone: string) => void
) {
  const { user } = useAuth();
  const { currentOrg } = useOrganizationContext();
  
  // Refs para valores que mudam frequentemente (evita closures stale)
  const selectedChatIdRef = useRef<string | null>(null);
  const selectedChatPhoneRef = useRef<string | null>(null);
  const onChatSelectRef = useRef<((chatId: string, phone: string) => void) | undefined>(undefined);
  
  // Refs para controle de notificações
  const notifiedMessagesRef = useRef<Set<string>>(new Set());
  const openChatPhonesRef = useRef<Set<string>>(new Set());
  const channelRef = useRef<any>(null);
  const isSettingUpRef = useRef(false);
  const lastOrgIdRef = useRef<string | null>(null);
  const lastUserIdRef = useRef<string | null>(null);
  const notificationPermissionRef = useRef<NotificationPermission>('default');
  const toastDebounceRef = useRef<Map<string, number>>(new Map()); // Para evitar toasts duplicados

  // Atualizar refs sempre que os valores mudarem
  useEffect(() => {
    selectedChatIdRef.current = selectedChatId;
    selectedChatPhoneRef.current = selectedChatPhone;
    onChatSelectRef.current = onChatSelect;
  }, [selectedChatId, selectedChatPhone, onChatSelect]);

  // Atualizar lista de chats abertos de forma otimizada
  useEffect(() => {
    if (selectedChatPhone && currentOrg?.id) {
      const normalizedPhone = normalizePhone(selectedChatPhone);
      if (normalizedPhone) {
        openChatPhonesRef.current.clear(); // Limpar e adicionar apenas o atual
        openChatPhonesRef.current.add(normalizedPhone);
        if (selectedChatId) {
          // Manter apenas o chat atual aberto
        }
      }
    } else {
      openChatPhonesRef.current.clear();
    }
  }, [selectedChatId, selectedChatPhone, currentOrg?.id]);

  // Solicitar permissão de notificações push quando o componente montar
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!('Notification' in window)) {
      console.log('⚠️ Notificações push não suportadas neste navegador');
      return;
    }

    const isSecureContext = window.isSecureContext || 
                           window.location.protocol === 'https:' || 
                           window.location.hostname === 'localhost' ||
                           window.location.hostname === '127.0.0.1' ||
                           window.location.hostname === '[::1]';

    if (!isSecureContext) {
      console.warn('⚠️ Notificações push requerem contexto seguro (HTTPS ou localhost)');
      return;
    }

    notificationPermissionRef.current = Notification.permission;

    const requestNotificationPermission = async () => {
      try {
        if (!('Notification' in window)) {
          return;
        }

        if (Notification.permission === 'default') {
          const permission = await Notification.requestPermission();
          notificationPermissionRef.current = permission;
          
          if (permission === 'granted') {
            console.log('✅ Permissão de notificações push concedida');
          } else if (permission === 'denied') {
            console.log('❌ Permissão de notificações push negada pelo usuário');
          }
        } else if (Notification.permission === 'granted') {
          notificationPermissionRef.current = 'granted';
        }
      } catch (error) {
        console.error('❌ Erro ao solicitar permissão de notificações:', error);
      }
    };

    const timeoutId = setTimeout(() => {
      requestNotificationPermission();
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  // Função auxiliar para verificar se o chat está aberto (usando refs para valores atualizados)
  const isChatOpen = useCallback((messagePhone: string): boolean => {
    const currentSelectedPhone = selectedChatPhoneRef.current;
    if (!currentSelectedPhone) {
      return false;
    }
    
    const normalizedSelected = normalizePhone(currentSelectedPhone);
    const normalizedMessage = normalizePhone(messagePhone);
    
    return normalizedSelected === normalizedMessage && normalizedSelected.length > 0;
  }, []);

  // Função para exibir toast de forma otimizada (com debounce)
  const showToastNotification = useCallback((
    contactName: string,
    messagePreview: string,
    messagePhone: string,
    messageId: string
  ) => {
    console.log('🔔 showToastNotification chamada:', {
      contactName,
      messagePreview,
      messagePhone,
      messageId
    });
    
    // Debounce: evitar múltiplos toasts para a mesma mensagem ou chat em curto período
    const now = Date.now();
    const lastToastTime = toastDebounceRef.current.get(messagePhone) || 0;
    const debounceDelay = 2000; // 2 segundos entre toasts do mesmo chat
    
    if (now - lastToastTime < debounceDelay) {
      console.log('⏭️ Toast ignorado por debounce:', messagePhone, 'último toast há', now - lastToastTime, 'ms');
      return;
    }
    
    // REMOVIDO: Verificação dupla estava bloqueando o toast
    // A verificação já foi feita antes de chamar esta função
    // Se chegou aqui, é porque o chat não está aberto
    
    console.log('✅ Todas as verificações passaram - exibindo toast...');
    toastDebounceRef.current.set(messagePhone, now);
    
    // Limpar debounce após 5 segundos
    setTimeout(() => {
      toastDebounceRef.current.delete(messagePhone);
    }, 5000);

    try {
      console.log('🎨 Criando toast customizado...');
      const toastId = toast.custom((t) => (
        <div
          className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 min-w-[320px] max-w-[400px] transition-all z-[9999] ${
            t.visible ? 'animate-in slide-in-from-top-5' : 'animate-out slide-out-to-top-5'
          }`}
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-primary" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground">
                Nova mensagem
              </p>
              <p className="text-sm font-medium text-foreground mt-0.5">
                {contactName}
              </p>
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                {messagePreview}
              </p>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const currentOnChatSelect = onChatSelectRef.current;
                    if (currentOnChatSelect && messagePhone) {
                      currentOnChatSelect(messagePhone, messagePhone);
                    }
                    toast.dismiss(t.id);
                  }}
                  className="flex-1 px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                >
                  Abrir Chat
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toast.dismiss(t.id);
                  }}
                  className="px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      ), {
        duration: 3000,
        position: 'top-right',
        id: `chat-notification-${messageId}`,
      });

      console.log('✅ Toast exibido com sucesso:', toastId);
    } catch (toastError) {
      console.error('❌ Erro ao exibir toast:', toastError);
      // Fallback: toast simples
      toast.success(`Nova mensagem de ${contactName}: ${messagePreview}`, {
        duration: 3000,
        position: 'top-right',
        action: {
          label: 'Abrir',
          onClick: () => {
            const currentOnChatSelect = onChatSelectRef.current;
            if (currentOnChatSelect && messagePhone) {
              currentOnChatSelect(messagePhone, messagePhone);
            }
          }
        }
      });
    }
  }, [isChatOpen]);

  // Função para exibir notificação push do navegador
  const showPushNotification = useCallback((
    contactName: string,
    messagePreview: string,
    messagePhone: string
  ) => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return false;
    }

    if (Notification.permission !== 'granted') {
      return false;
    }

    const isSecureContext = window.isSecureContext || 
                           window.location.protocol === 'https:' || 
                           window.location.hostname === 'localhost' ||
                           window.location.hostname === '127.0.0.1' ||
                           window.location.hostname === '[::1]';

    if (!isSecureContext) {
      return false;
    }

    try {
      const notification = new Notification(`Nova mensagem de ${contactName}`, {
        body: messagePreview,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: `chat-${messagePhone}`,
        requireInteraction: false,
        silent: false,
        data: {
          phone: messagePhone,
          contactName: contactName,
        }
      });

      notification.onclick = (event) => {
        event.preventDefault();
        if (window.focus) {
          window.focus();
        }
        notification.close();
        
        setTimeout(() => {
          const currentOnChatSelect = onChatSelectRef.current;
          if (currentOnChatSelect && messagePhone) {
            currentOnChatSelect(messagePhone, messagePhone);
          }
        }, 100);
      };

      notification.onerror = (error) => {
        console.error('❌ Erro na notificação push:', error);
      };

      setTimeout(() => {
        try {
          notification.close();
        } catch (closeError) {
          // Ignorar erros ao fechar
        }
      }, 8000);

      return true;
    } catch (error) {
      console.error('❌ Erro ao criar notificação push:', error);
      return false;
    }
  }, []);

  // Configurar subscription de notificações
  useEffect(() => {
    if (!user?.id || !currentOrg?.id) {
      return;
    }

    // Evitar múltiplas configurações simultâneas
    if (isSettingUpRef.current && lastOrgIdRef.current === currentOrg.id && lastUserIdRef.current === user.id) {
      return;
    }

    // Limpar subscription anterior
    if (channelRef.current) {
      console.log('🔌 Removendo subscription anterior');
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    isSettingUpRef.current = true;
    lastOrgIdRef.current = currentOrg.id;
    lastUserIdRef.current = user.id;

    let channel: any = null;

    const fetchAssignedContacts = async () => {
      // Atribuição está em chats_Instagram (user_atribuido), não em contacts
      const { data: assignedChats, error: chatsError } = await chatSupabase
        .from('conversations')
        .select('contact_id')
        .eq('org_id', currentOrg.id)
        .eq('user_atribuido', user.id);

      if (chatsError) {
        console.error('❌ Erro ao buscar chats atribuídos:', chatsError);
        isSettingUpRef.current = false;
        return;
      }

      const contactIds = [...new Set((assignedChats || []).map(c => c.contact_id).filter(Boolean))] as string[];
      if (contactIds.length === 0) {
        isSettingUpRef.current = false;
        return;
      }

      const { data: contacts, error: contactsError } = await supabase
        .from('contacts')
        .select('id, phone, name')
        .in('id', contactIds);

      if (contactsError) {
        console.error('❌ Erro ao buscar contatos:', contactsError);
        isSettingUpRef.current = false;
        return;
      }

      const assignedPhones = (contacts || []).map(c => {
        const normalized = normalizePhone(c.phone || '');
        return normalized;
      }).filter(p => p.length > 0);
      
      if (assignedPhones.length === 0) {
        console.log('⚠️ Nenhum telefone válido encontrado nos contatos atribuídos');
        isSettingUpRef.current = false;
        return;
      }

      // Buscar instâncias da organização
      const { data: instances, error: instancesError } = await supabase
        .from('instances')
        .select('id')
        .eq('org_id', currentOrg.id)
        .in('status', ['open', 'not_validated']);

      if (instancesError) {
        console.error('❌ Erro ao buscar instâncias:', instancesError);
        isSettingUpRef.current = false;
        return;
      }

      if (!instances || instances.length === 0) {
        console.log('⚠️ Nenhuma instância encontrada');
        isSettingUpRef.current = false;
        return;
      }

      const instanceIds = instances.map(i => i.id);

      // Configurar subscription (timestamp garante nome único, evitando mismatch ao reconectar)
      const channelName = `chat-notifications-${user.id}-${currentOrg.id}-${Date.now()}`;
      const instanceIdSet = new Set(instanceIds);
      channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'chat',
            table: 'messages',
          },
          async (payload) => {
            // Só processar novos registros (INSERT), ignorar UPDATE/DELETE
            if (payload.eventType !== 'INSERT') return;

            const newMessage = payload.new as any;

            // Filtrar por instâncias da org (client-side)
            if (!instanceIdSet.has(newMessage.instance_id)) return;

            const msgId = newMessage.message_id ?? newMessage.id;

            // chat_messages_instagram: mensagem do cliente (não Bot/Operador/Sistema e não nota interna)
            if (newMessage.is_internal) return;
            const sender = (newMessage.user_sender || '').toString();
            const isFromClient = !['Bot', 'Operador', 'Sistema'].includes(sender) && sender !== '';

            if (!isFromClient) return;

            const messagePhone = normalizePhone(newMessage.origem_contato || newMessage.phone || '');
            if (!messagePhone || messagePhone.length === 0) return;

            const isAssigned = assignedPhones.includes(messagePhone);
            if (!isAssigned) return;

            if (msgId && notifiedMessagesRef.current.has(msgId)) return;
            if (msgId) notifiedMessagesRef.current.add(msgId);

            const contact = contacts.find(c => {
              const contactPhone = normalizePhone(c.phone || '');
              return contactPhone === messagePhone && contactPhone.length > 0;
            });
            const contactName = contact?.name || newMessage.origem_contato || newMessage.phone || 'Cliente';

            let messagePreview = '';
            const content = (newMessage.content_message || newMessage.user_message || '').trim();
            if (content) {
              messagePreview = content.length > 50 ? content.substring(0, 50) + '...' : content;
            } else if (newMessage.media_type) {
              const mediaType = newMessage.media_type;
              if (mediaType.startsWith('image/') || mediaType === 'image') {
                messagePreview = '📷 Imagem';
              } else if (mediaType.startsWith('video/') || mediaType === 'video') {
                messagePreview = '🎥 Vídeo';
              } else if (mediaType.startsWith('audio/') || mediaType === 'audio') {
                messagePreview = '🎵 Áudio';
              } else {
                messagePreview = `📎 ${newMessage.media_file_name || 'Arquivo'}`;
              }
            } else {
              messagePreview = 'Nova mensagem';
            }

            // Verificar se o chat está aberto (usando função otimizada)
            const chatIsOpen = isChatOpen(messagePhone);
            
            // Verificar estado da página
            const isPageVisible = !document.hidden && document.visibilityState === 'visible';
            const isPageFocused = document.hasFocus();
            
            console.log('🔔 Estado da notificação:', {
              chatIsOpen,
              messagePhone,
              selectedChatPhone: selectedChatPhoneRef.current,
              isPageVisible,
              isPageFocused,
              contactName,
              messagePreview
            });
            
            // Mostrar push notification se necessário
            const shouldShowPush = !isPageVisible || !isPageFocused || !chatIsOpen;
            if (shouldShowPush) {
              showPushNotification(contactName, messagePreview, messagePhone);
            }

            // TOAST: Sempre mostrar se o chat NÃO estiver aberto
            // IMPORTANTE: O toast deve aparecer sempre que houver uma nova mensagem de um chat não aberto
            if (!chatIsOpen) {
              console.log('✅ Chat não está aberto - chamando showToastNotification...');
              // Chamar diretamente sem verificação adicional dentro da função
              showToastNotification(contactName, messagePreview, messagePhone, newMessage.id);
            } else {
              console.log('⏭️ Chat está aberto - toast não será exibido');
            }

            // Criar notificação no banco de dados (não bloqueia)
            supabase.from('notifications').insert({
              user_id: user.id,
              type: 'new_message',
              title: 'Nova mensagem',
              message: `${contactName}: ${messagePreview}`,
              data: {
                chat_id: newMessage.phone,
                phone: newMessage.phone,
                contact_name: contactName,
                message_id: newMessage.id,
              },
              is_read: false,
            }).catch((error) => {
              console.error('❌ Erro ao criar notificação no banco:', error);
            });
          }
        )
        .subscribe((status, err) => {
          if (err) {
            console.error('❌ Erro na subscription:', err);
            isSettingUpRef.current = false;
          }
          if (status === 'SUBSCRIBED') {
            isSettingUpRef.current = false;
          } else if (status === 'CHANNEL_ERROR') {
            isSettingUpRef.current = false;
          } else if (status === 'TIMED_OUT') {
            isSettingUpRef.current = false;
            setTimeout(() => {
              if (channel) {
                channel.subscribe();
              }
            }, 1000);
          }
        });

      channelRef.current = channel;
    };

    fetchAssignedContacts();
    
    return () => {
      isSettingUpRef.current = false;
      if (channelRef.current) {
        console.log('🔌 Removendo subscription de notificações');
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [user?.id, currentOrg?.id, isChatOpen, showToastNotification, showPushNotification]);

  // Limpar mensagens notificadas periodicamente
  useEffect(() => {
    const interval = setInterval(() => {
      notifiedMessagesRef.current.clear();
      toastDebounceRef.current.clear();
    }, 60 * 60 * 1000); // 1 hora

    return () => clearInterval(interval);
  }, []);
}
